import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ToastContext } from 'contexts'
import { useMutation, useQuery } from '@apollo/client'
import getBalanceGql from 'gql/payment/getBalance.gql'
import getLatestTransactionsGql from 'gql/payment/getLatestTransactions.gql'
import addPaymentMethodGql from 'gql/payment/createPaymentCard.gql'
import payWithCardGql from 'gql/payment/payWithCard.gql'
import removeCardGql from 'gql/payment/removeCard.gql'
import updateAutoPaySettingsGql from 'gql/payment/updateAutoPaySettings.gql'
import paymentDetailsGql from 'gql/payment/paymentDetails.gql'
import getExplorerGql from 'gql/payment/getExplorer.gql'
import getCustomerIdGql from 'gql/payment/getCustomerId.gql'
import createStripeCustomerGql from 'gql/payment/createCustomer.gql'

/**
 * Returns a service hook for paying with a card.
 *
 * @return {Object} An object containing the following properties:
 *   - payWithCard: A function that takes in three parameters: customerId (string), amount (number), and currency (string).
 *     It returns a Promise that resolves to the data of the payment.
 *   - loading: A boolean indicating whether the data is currently being loaded.
 *
 * @throws {Error} If an error occurs while making the payment request.
 */
export const usePayWithCardService = () => {
    const { setToast } = useContext(ToastContext)
    const [mutation, { loading }] = useMutation(payWithCardGql)
    const { t } = useTranslation()

    const payWithCard = async (amount: number) => {
        try {
            const { data: payWithCard } = await mutation({
                variables: {
                    input: {
                        amount,
                    },
                },
            })

            return payWithCard.data
        } catch (e) {
            setToast({
                type: 'negative',
                message: t('An error while making payment request'),
                open: true,
            })
            throw e
        }
    }

    return { payWithCard, loading }
}

/**
 * Generates a service hook for removing a payment card.
 *
 * @return {Object} An object containing the following properties:
 *   - removePaymentCard: A function that removes a payment card.
 *     It takes in two parameters: customer_id (string) and payment_method_id (string).
 *     It returns a Promise that resolves to the result of the mutation.
 *   - loading: A boolean indicating whether the data is currently being loaded.
 *
 * @throws {Error} If an error occurs while removing the card.
 */
export const useRemovePaymentCardService = () => {
    const { setToast } = useContext(ToastContext)
    const [mutation, { loading }] = useMutation(removeCardGql)
    const { t } = useTranslation()

    const removePaymentCard = async () => {
        try {
            const { data } = await mutation({
                variables: {
                    input: {},
                },
            })

            setToast({
                type: 'positive',
                message: t('Your card has been removed'),
                open: true,
            })

            return data.removeCard
        } catch (e) {
            setToast({
                type: 'negative',
                message: t('An error occurred while removing card'),
                open: true,
            })
            throw e
        }
    }

    return { removePaymentCard, loading }
}

/**
 * Retrieves the balance for the user's account.
 *
 * @return {Object} An object containing the following properties:
 *   - data: The total balance of the user's account. If no balance is found, an empty array is returned.
 *   - error: An error object if there was an error retrieving the balance.
 *   - loading: A boolean indicating whether the data is currently being loaded.
 *   - refetch: A function to manually refetch the data.
 */
export const useGetBalanceService = () => {
    const { data, error, loading, refetch } = useQuery(getBalanceGql)

    return {
        data: data?.getBalance?.total_balance || null,
        error,
        loading,
        refetch,
    }
}

/**
 * Retrieves the latest transactions for the user's account.
 *
 * @return {Object} An object containing the following properties:
 *   - data: An array of the latest transactions. If no transactions are found, an empty array is returned.
 *   - error: An error object if there was an error retrieving the transactions.
 *   - loading: A boolean indicating whether the data is currently being loaded.
 *   - refetch: A function to manually refetch the data.
 */
export const useGetLatestTransactionsService = () => {
    const { data, error, loading, refetch } = useQuery(getLatestTransactionsGql)

    return {
        data: data?.getLatestTransactions || [],
        error,
        loading,
        refetch,
    }
}

export const useGetExplorerService = (filter: { page: number; limit: number }) => {
    const { data, error, loading, refetch } = useQuery(getExplorerGql, {
        variables: { ...filter },
    })

    return {
        data: data?.getExplorer || { items: [], total_count: 0 },
        error,
        loading,
        refetch,
    }
}

/**
 * Returns an object containing a function to create a payment card and a loading state.
 *
 * @return {Object} An object with the following properties:
 *   - createCard: A function that takes a customer ID as a parameter and returns a Promise that resolves to the data of the created payment card.
 *   - loading: A boolean indicating if the mutation is currently loading.
 * @throws {Error} Throws an error if there was an error while creating the payment card.
 */
export const useAddCardService = () => {
    const { setToast } = useContext(ToastContext)
    const { t } = useTranslation()
    const [addPaymentMethodMutation, { loading }] = useMutation(addPaymentMethodGql)

    /**
     * Creates a payment card for a given customer ID.
     *
     * @param {string} customer_id - The ID of the customer.
     * @return {Promise<any>} A Promise that resolves to the data of the created payment card.
     * @throws {Error} If an error occurs while adding the card.
     */
    const createCard = async (): Promise<{ url: string }> => {
        try {
            const { data: addPaymentMethod } = await addPaymentMethodMutation({
                variables: { input: {} },
            })

            return addPaymentMethod.data
        } catch (error) {
            console.log('error', error)
            setToast({
                type: 'negative',
                message: t(`An error occurred while adding card ${error}`),
                open: true,
            })
            throw error
        }
    }

    return {
        createCard,
        loading,
    }
}

/**
 * Retrieves the payment details for the current user's account.
 *
 * @return {Object} An object containing the payment details, an error object (if any), and a loading flag.
 * - `data`: An object containing the payment details. It has the following properties:
 *   - `auto_pay_threshold`: The auto-pay threshold amount.
 *   - `auto_pay_amount`: The auto-pay amount.
 *   - `is_card_added`: A boolean indicating whether a card is added or not.
 * - `error`: An error object (if any).
 * - `loading`: A boolean indicating whether the query is currently loading.
 */
export const usePaymentDetails = () => {
    const { data, error, loading } = useQuery(paymentDetailsGql)

    return {
        data: data?.paymentDetails || {},
        error,
        loading,
    }
}

export interface UseUpdateAutoPaySettings {
    updateAutoPaySettings: (input: {
        auto_pay_threshold: number
        auto_pay_amount: number
    }) => Promise<{ updateAutoPaySettings: { message: string } }>
    loading: boolean
}

/**
 * Returns an object with an `updateAutoPaySettings` function and a `loading` boolean.
 * The `updateAutoPaySettings` function updates the auto-pay settings for the current user's account.
 * It takes an object with `auto_pay_threshold` and `auto_pay_amount` properties as input.
 * The function returns a promise that resolves to an object with an `updateAutoPaySettings` property.
 * If the update is successful, a toast notification is shown with a positive message.
 * If an error occurs, a toast notification is shown with a negative message.
 *
 * @return {Object} An object with the following properties:
 * - `updateAutoPaySettings`: A function that updates the auto-pay settings.
 * - `loading`: A boolean indicating whether the mutation is currently loading.
 */
export const useUpdateAutoPaySettingsService = () => {
    const { setToast } = useContext(ToastContext)
    const [mutation, { loading }] = useMutation(updateAutoPaySettingsGql)
    const { t } = useTranslation()

    /**
     * Updates the auto-pay settings for the current user's account.
     *
     * @param {Object} input - An object containing the auto-pay threshold and amount.
     * @param {number} input.auto_pay_threshold - The auto-pay threshold amount.
     * @param {number} input.auto_pay_amount - The auto-pay amount.
     * @return {Promise<UseUpdateAutoPaySettings>} A promise that resolves to an object with an `updateAutoPaySettings` property.
     * If the update is successful, a toast notification is shown with a positive message.
     * If an error occurs, a toast notification is shown with a negative message.
     * @throws {Error} If an error occurs while updating the auto-pay settings.
     */
    const updateAutoPaySettings = async (input: {
        auto_pay_threshold: number
        auto_pay_amount: number
    }): Promise<UseUpdateAutoPaySettings> => {
        try {
            const { data } = await mutation({
                variables: {
                    input: { ...input },
                },
            })

            setToast({
                type: 'positive',
                message: data.updateAutoPaySettings.message,
                open: true,
            })

            return data.updateAutoPaySettings
        } catch (e) {
            setToast({
                type: 'negative',
                message: t('An error occurred while updating Auto-Pay settings'),
                open: true,
            })
            throw e
        }
    }

    return { updateAutoPaySettings, loading }
}

export const useCustomerIdService = ({ account_id }: { account_id: string }) => {
    const { data, error, loading, refetch } = useQuery(getCustomerIdGql, {
        variables: { account_id },
        skip: !account_id,
        fetchPolicy: 'cache-and-network',
    })

    return {
        data: data?.getCustomerId || [],
        error,
        loading,
        refetch,
    }
}

export const useCreateStripeCustomerService = () => {
    const { setToast } = useContext(ToastContext)
    const [mutation, { loading }] = useMutation(createStripeCustomerGql)

    const createStripeCustomer = async () => {
        try {
            const {
                data: { createCustomer },
            } = await mutation({
                variables: { input: {} },
            })

            return createCustomer
        } catch (error) {
            setToast({
                message: error?.message ?? 'Error creating Stripe Customer',
                type: 'negative',
                open: true,
            })
            throw error // Rethrow the error to propagate it to the caller
        }
    }

    return { createStripeCustomer, loading }
}
